// Advanced Capacity Mode disaster behaviour.
// Canonical source: design/canonical-numbers.md section 16.6.
// Classic disaster data (src/data/disasters.js) is untouched; this
// file layers Advanced Mode behaviour on top, keyed by disaster id,
// and defines the Advanced-only cards and the Advanced draw pool.
//
// Each descriptor says which channels a disaster touches in Advanced
// Mode. Output-multiplier disasters reuse their Classic factors
// (including two-tier severity and per-plant factors) for annual
// generation. The firm capacity channel is SOFTENED by default:
// firmFactor = (1 + generationFactor) / 2, except a generation
// factor of 0 (offline) which stays 0. Firm capacity is a planning
// credit for the peak hour; a bad output year dents it less than it
// dents annual energy. The softening rule lives in
// src/engine/advancedCapacity.js (firmFactorFromGenerationFactor).
//
// Recognised fields:
//   affectsGeneration:   apply the output multiplier to annual
//                        generation (default true for
//                        output-multiplier effects)
//   affectsFirmCapacity: apply a multiplier to firm capacity
//                        (default true for output-multiplier
//                        effects, softened as above)
//   firmCapacityFactor:  optional override factor for the firm
//                        capacity channel (bypasses softening)
//   firmDerate:          extra firm-capacity-only hit, independent
//                        of the Classic effect kind:
//                        { families: [...], factor } applied to
//                        every owned plant in those families
//   peakDemandAddByYear: extra peak demand in GW
//   costOnly / policyOnly: documentation flags; cost and policy
//                        effects flow through the Classic effect
//                        kinds (opex-multiplier, subsidy-remove)

import {
  DISASTERS,
  DISASTER_TIER,
  DISASTER_CATEGORY,
  EFFECT_KIND,
  TARGET_KIND
} from './disasters.js';
import { FAMILY } from './families.js';

export const ADVANCED_DISASTER_EFFECTS = {
  'fossil-fuel-supply-crisis': {
    affectsGeneration: true,
    affectsFirmCapacity: true
  },
  'grid-connection-constraint': {
    affectsGeneration: true,
    affectsFirmCapacity: true
  },
  'supply-chain-bottleneck': {
    affectsGeneration: true,
    affectsFirmCapacity: true
  },
  'fossil-fuel-price-shock': {
    // Cost as in Classic, plus a peak-availability hit: fuel you
    // cannot afford is fuel you cannot dispatch at peak.
    costOnly: true,
    firmDerate: { families: [FAMILY.FOSSIL_THERMAL], factor: 0.9 }
  },
  'nuclear-safety-audit': {
    // An audit forces real outages: the firm channel takes the full
    // Classic factor rather than the softened default.
    affectsGeneration: true,
    affectsFirmCapacity: true,
    firmCapacityFactor: 0.75
  },
  'nuclear-public-outcry': {
    // Political pressure forces the reactors to actually run at the
    // restricted level, peak hours included: firm reuses the
    // per-plant generation factors (0.5 Large / 0.85 SMR).
    affectsGeneration: true,
    affectsFirmCapacity: true,
    firmUsesGenerationFactor: true
  },
  'solar-panel-recall': {
    affectsGeneration: true,
    affectsFirmCapacity: true
  },
  'wind-turbine-defect': {
    affectsGeneration: true,
    affectsFirmCapacity: true
  },
  'tidal-equipment-failure': {
    affectsGeneration: true,
    affectsFirmCapacity: true
  },
  'severe-drought': {
    affectsGeneration: true,
    affectsFirmCapacity: true
  },
  'extreme-weather-event': {
    affectsGeneration: true,
    affectsFirmCapacity: true
  },
  'demand-spike': {
    // Heatwave / cold snap: TWh reuse the Classic annual
    // demand spike values (+3/+5/+7) via the Classic demand-add
    // effect; peak demand rises and thermal plants derate at peak
    // (cooling limits, fuel-handling stress).
    peakDemandAddByYear: { 1: 1, 2: 2, 3: 3 },
    firmDerate: [
      { families: [FAMILY.FOSSIL_THERMAL], factor: 0.85 },
      // Nuclear cooling limits bite harder in extreme temperatures.
      { families: [FAMILY.NUCLEAR], factor: 0.75 }
    ]
  },
  'subsidy-reform': {
    policyOnly: true
  },
  'storage-facility-fault': {
    // Advanced-only card (below): firm capacity channel only.
    // Storage generates nothing, so the generation channel is moot.
    affectsGeneration: false,
    affectsFirmCapacity: true,
    firmCapacityFactor: 0.5
  },
  'induced-seismicity': {
    // Advanced-only card (below). A seismicity review takes the
    // field offline rather than throttling it, so the firm channel
    // reuses the generation factor instead of the softened default
    // — same treatment as the nuclear cards.
    affectsGeneration: true,
    affectsFirmCapacity: true,
    firmUsesGenerationFactor: true
  }
};

// Classic cards that do NOT enter the Advanced deck. Coal Import Ban
// is dropped because Advanced already covers Fossil Thermal through
// the supply crisis, the price shock and the heatwave derate, while
// the Geothermal family had no exposure at all — Induced Seismicity
// takes its slot. Removing a card rather than adding one keeps the
// deck at 15 so no other card's draw probability moves.
const ADVANCED_EXCLUDED_CARD_IDS = new Set(['coal-import-ban']);

// Advanced-only disaster cards. These never enter the Classic deck.
export const ADVANCED_ONLY_DISASTERS = [
  {
    // Targets Grid Battery only. Long Duration Storage is deliberately
    // immune, and that immunity is the plant's reason to exist: it
    // was viable but never optimal at every price tried, because it
    // has lower firm capacity, a bigger charge requirement and second
    // place in the charging queue against one advantage - the carry -
    // that measured worth nothing (section 16.5.4). Immunity closes
    // most of the gap and gives the two stores genuinely different
    // risk, which no price could.
    //
    // The real-world reading carries it: grid-scale lithium fires and
    // safety recalls are a battery-specific failure (Moss Landing
    // 2021 and 2022, Victoria Big Battery 2021). Pumped hydro, thermal
    // and flow storage do not share that mode. Same reasoning shape as
    // Induced Seismicity targeting Geothermal by plant, not by family.
    id: 'storage-facility-fault',
    number: 15,
    name: 'Battery Safety Recall',
    // Explicit because DisasterCard's fallback is
    // /disaster-images/<id>.webp, which does not exist for either
    // Advanced-only card. Netlify serves from a case-sensitive
    // filesystem, so this must match the file byte-for-byte.
    imageSrc: '/disaster-images/Battery_Recall.webp',
    description:
      'A cell fire prompts a safety recall, forcing grid batteries to run at half capability this year. Long-duration storage is unaffected.',
    tier: DISASTER_TIER.PARTIAL,
    category: DISASTER_CATEGORY.TECHNICAL,
    target: {
      kind: TARGET_KIND.PLANTS,
      ids: ['grid-battery']
    },
    effect: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.5 }
  },
  {
    // Every other family carries at least one dedicated card;
    // Alternative Clean carried none, which made Geothermal the one
    // technology the deck could never touch, and so the safest thing
    // to hold and to duplicate.
    // Induced seismicity is its real signature risk — Basel (2006)
    // and Pohang (2017) both ended in cancelled projects — and it is
    // a social-licence failure like Nuclear Public Outcry rather than
    // an equipment fault. Targets Geothermal by id, not by family:
    // Hydrogen shares the Alternative Clean family and has nothing to
    // do with drilling-induced earthquakes.
    id: 'induced-seismicity',
    number: 16,
    name: 'Induced Seismicity',
    imageSrc: '/disaster-images/Seismic_Activity.webp',
    description:
      'Geothermal drilling sets off a swarm of small earthquakes. Public opposition forces the field to half output pending review.',
    tier: DISASTER_TIER.PARTIAL,
    category: DISASTER_CATEGORY.SAFETY_POLITICAL,
    target: {
      kind: TARGET_KIND.PLANTS,
      ids: ['geothermal']
    },
    effect: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.5 }
  }
];

// Advanced Mode overrides for shared cards. Mostly player-facing
// copy; Gas & Oil Supply Crisis also re-targets, because it is the
// only fossil card in the Advanced deck that targets by plant id
// rather than by family, and the legacy fleet would otherwise be
// immune to it. Family-targeted cards (the price shock's fuel
// squeeze, the heatwave thermal derate, largest-family selection)
// already reach the legacy plants through their family.
//
// Classic data is untouched: Coal Import Ban is not in the Advanced
// deck, so no coal-id card needs the same treatment.
const ADVANCED_CARD_OVERRIDES = {
  'fossil-fuel-supply-crisis': {
    target: {
      kind: TARGET_KIND.PLANTS,
      ids: [
        'diesel-generator',
        'combined-cycle-gas',
        'legacy-diesel-generator'
      ]
    }
  },
  'demand-spike': {
    description:
      'A heatwave or cold snap drives record demand and strains thermal plants at peak.'
  },
  'fossil-fuel-price-shock': {
    description:
      'Global fuel markets surge, doubling fossil operating costs and squeezing fuel available at peak.'
  }
};

// The Advanced Mode draw pool: 13 of the 14 Classic cards (with
// Advanced wording overrides, minus the excluded ones) plus the two
// Advanced-only cards, so 15 in total. Draw counts per year are
// unchanged (1 / 2 / 3).
export const ADVANCED_DISASTER_POOL = [
  ...DISASTERS.filter((d) => !ADVANCED_EXCLUDED_CARD_IDS.has(d.id)).map((d) => ({
    ...d,
    ...(ADVANCED_CARD_OVERRIDES[d.id] ?? {})
  })),
  ...ADVANCED_ONLY_DISASTERS
];

export const ADVANCED_DISASTER_BY_ID = Object.fromEntries(
  ADVANCED_DISASTER_POOL.map((d) => [d.id, d])
);
